import { useEffect, useState } from "react";

import {
    ListContainer,
    ListContext,
    Page,
    PageRoute,
    SearchBar,
    Stack,
    buildPath,
    buildTemplatePath,
    pn
} from "@compass/core";

import { ListFooter } from "components";
import {
    Permission,
    DiplomaTypeListDto,
    DiplomaTypeListResource,
    TopicListFilter
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { TopicFilter } from "../TopicFilter";
import { TopicListPageHeader } from "./TopicListPageHeader";
import { TopicTable } from "./TopicTable";

const routeName = "topics";

export const topicListPageRoute: PageRoute = {
    handle: "Topics",
    templatePath: buildTemplatePath(routeName),
    getPath: () => buildPath(routeName),
};

export const TopicListPage = () => {
    const ROOT_PERMISSION: Permission = Permission.SystemUtility;

    const [listItems, setListItems] = useState<DiplomaTypeListDto[]>([]);
    const { listFilter, setListFilter } = useInitListFilter<TopicListFilter>({
        sortObjects: [{ property: pn<DiplomaTypeListDto>("name"), isDesc: false }],
    });
    const [initListFilter] = useState<TopicListFilter>(listFilter);
    const [total, setTotal] = useState<number>();
    const [resource, setResource] = useState<DiplomaTypeListResource>({});
    const [searchFocused, setSearchFocused] = useState<boolean>(false);

    const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
        listFilter: listFilter,
        setListFilter: setListFilter,
        fetchList: (listFilter) => {
            ApiService.topics.getList(listFilter).then((response) => {
                setListItems(response);
            });
        },
        fetchTotal: (listFilter) => {
            setTotal(undefined);
            ApiService.topics.getListTotal(listFilter).then((response) => {
                setTotal(response);
            });
        },
    });

    useEffect(() => {
        loadList(listFilter);
        ApiService.topics.getListResource().then((response) => {
            setResource(response);
        });
    }, []);

    const listFilterNode = useListFilter<TopicListFilter>({
        listFilter: listFilter,
        initListFilter: initListFilter,
        onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
        inputNode: (listFilter, setListFilter) => (
            <TopicFilter
                filter={listFilter.topicFilter || {}}
                setFilter={(filter) =>
                    setListFilter(
                        produce(listFilter, (draft) => {
                            draft.topicFilter = filter;
                        })
                    )
                }
            />
        ),
    });

    return (
        <>
            <Page
                header={
                    <TopicListPageHeader
                        rootPermission={ROOT_PERMISSION}
                        handle={topicListPageRoute.handle}
                    />
                }
            >
                <ListContainer
                    fullHeight
                    header={
                        <Stack direction="row" justifyContent="space-between">
                            <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
                            {listFilterNode}
                        </Stack>
                    }
                    tableContainer={
                        <TopicTable
                            rootPermission={ROOT_PERMISSION}
                            tableContainerRef={tableContainerRef}
                            topics={listItems}
                            setTopics={setListItems}
                            resource={resource}
                            searchFocused={searchFocused}
                            sortObjects={listFilter.sortObjects}
                            sort={applySort}
                            loadList={() => loadList(listFilter)}
                        />
                    }
                    footer={
                        <ListFooter
                            total={total}
                            size={listFilter.pageSize}
                            page={listFilter.page}
                            onPageChange={applyPage}
                            onSizeChange={applyPageSize}
                            onSortMethodChange={applySortMethod}
                        />
                    }
                />
            </Page>

            <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
                <Outlet />
            </ListContext.Provider>
        </>
    );
};
